import { useEffect, useRef, useState } from "react"

import "./index.scss"

// RELOAD-BANNER 2026-07-03: rapid dev redeploys rotate hashed bundles/chunks out from under
// tabs that are already open. A stale tab either throws on a dynamic import of a chunk that
// rsync has since removed (tripping the app-shell ErrorBoundary -- "something went wrong" on
// ANY click) or silently keeps running stale logic. This banner detects both failure modes and
// offers a one-click reload. Deliberately independent of Redux/auth/context -- it must keep
// working on a stale tab even if the rest of the app's state is broken or the user is logged
// out, and it must never itself be able to crash the app (every listener/fetch is try/catched,
// it renders null until triggered).
//
// Test hook: window.__NVB_TEST can override timing without touching real timers --
//   window.__NVB_TEST = { pollMs: 500, dismissMs: 500 }
// Used by the shortened-interval verification pass; absent in production.

const DEFAULT_POLL_MS = 60_000
const DEFAULT_DISMISS_MS = 5 * 60_000
const CHUNK_ERROR_RE = /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported/i

function getTestOverrides() {
  try {
    return (window as any).__NVB_TEST || {}
  } catch (e) {
    return {}
  }
}

function getBootedHash(): string | null {
  try {
    const script = document.querySelector('script[src*="static/js/main."]')
    const src = script?.getAttribute("src") || ""
    const match = src.match(/main\.([a-z0-9]+)\.js/i)
    return match ? match[1] : null
  } catch (e) {
    return null
  }
}

async function getLiveHash(): Promise<string | null> {
  try {
    const res = await fetch("/", { cache: "no-store" })
    const text = await res.text()
    const match = text.match(/main\.([a-z0-9]+)\.js/i)
    return match ? match[1] : null
  } catch (e) {
    return null
  }
}

function isStaticChunkTarget(target: any): boolean {
  try {
    const src: string =
      target?.src || target?.href || (typeof target === "string" ? target : "") || ""
    return /\/static\/js\/.*\.chunk\.js/i.test(src)
  } catch (e) {
    return false
  }
}

const NewVersionBanner = () => {
  const [visible, setVisible] = useState(false)
  const bootedHashRef = useRef<string | null>(null)
  const dismissedUntilRef = useRef<number>(0)
  const staleRef = useRef(false)

  useEffect(() => {
    try {
      bootedHashRef.current = getBootedHash()
    } catch (e) {
      /* never let boot detection break the app */
    }

    const showIfNotDismissed = () => {
      try {
        if (Date.now() < dismissedUntilRef.current) {
          staleRef.current = true
          return
        }
        setVisible(true)
      } catch (e) {
        /* ignore */
      }
    }

    const checkVersion = async () => {
      try {
        if (!bootedHashRef.current) return
        const liveHash = await getLiveHash()
        if (liveHash && liveHash !== bootedHashRef.current) {
          staleRef.current = true
          showIfNotDismissed()
        }
      } catch (e) {
        /* fetch/parse failure -- never throw from the poll */
      }
    }

    const handleWindowError = (event: Event | any) => {
      try {
        const target = event?.target
        if (target && isStaticChunkTarget(target)) {
          staleRef.current = true
          showIfNotDismissed()
        }
      } catch (e) {
        /* ignore */
      }
    }

    const handleUnhandledRejection = (event: any) => {
      try {
        const reason = event?.reason
        const message =
          typeof reason === "string" ? reason : reason?.message || reason?.name || ""
        if (CHUNK_ERROR_RE.test(String(message))) {
          staleRef.current = true
          showIfNotDismissed()
        }
      } catch (e) {
        /* ignore */
      }
    }

    const handleVisibility = () => {
      try {
        if (document.visibilityState === "visible") {
          checkVersion()
        }
      } catch (e) {
        /* ignore */
      }
    }

    const handleOnline = () => {
      try {
        checkVersion()
      } catch (e) {
        /* ignore */
      }
    }

    let intervalId: ReturnType<typeof setInterval> | undefined
    try {
      const { pollMs } = getTestOverrides()
      intervalId = setInterval(checkVersion, pollMs || DEFAULT_POLL_MS)
    } catch (e) {
      /* ignore */
    }

    try {
      // capture=true: script/chunk load failures fire as non-bubbling "error" events on the
      // <script>/<link> element itself, so they only reach a capturing listener on window.
      window.addEventListener("error", handleWindowError, true)
      window.addEventListener("unhandledrejection", handleUnhandledRejection)
      document.addEventListener("visibilitychange", handleVisibility)
      window.addEventListener("online", handleOnline)
    } catch (e) {
      /* ignore */
    }

    return () => {
      try {
        if (intervalId) clearInterval(intervalId)
        window.removeEventListener("error", handleWindowError, true)
        window.removeEventListener("unhandledrejection", handleUnhandledRejection)
        document.removeEventListener("visibilitychange", handleVisibility)
        window.removeEventListener("online", handleOnline)
      } catch (e) {
        /* ignore */
      }
    }
  }, [])

  const handleReload = () => {
    try {
      window.location.reload()
    } catch (e) {
      /* ignore */
    }
  }

  const handleDismiss = () => {
    try {
      const { dismissMs } = getTestOverrides()
      dismissedUntilRef.current = Date.now() + (dismissMs || DEFAULT_DISMISS_MS)
      setVisible(false)
      setTimeout(() => {
        try {
          if (staleRef.current && Date.now() >= dismissedUntilRef.current) {
            setVisible(true)
          }
        } catch (e) {
          /* ignore */
        }
      }, (dismissMs || DEFAULT_DISMISS_MS) + 100)
    } catch (e) {
      /* ignore */
    }
  }

  if (!visible) return null

  return (
    <div className="new-version-banner" data-testid="new-version-banner">
      <span className="new-version-banner__text">
        A new version of CooWe is available.
      </span>
      <button
        type="button"
        className="new-version-banner__reload"
        data-testid="new-version-reload"
        onClick={handleReload}>
        Reload
      </button>
      <button
        type="button"
        className="new-version-banner__dismiss"
        aria-label="Dismiss"
        onClick={handleDismiss}>
        &times;
      </button>
    </div>
  )
}

export default NewVersionBanner
